import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, languages, defaultLanguage, getTranslation } from '../i18n';
import { getLanguagePreference, saveLanguagePreference } from '../services/preferencesService';

const LanguageContext = createContext();

// Get visible languages from localStorage
const getInitialVisibleLanguages = () => {
  const stored = localStorage.getItem('cabazaVisibleLanguages');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {}
  }
  // Default: all languages visible
  return languages.map(l => l.code);
};

export const LanguageProvider = ({ children }) => {
  // Get initial language from localStorage or browser preference
  const getInitialLanguage = () => {
    const savedLang = localStorage.getItem('language');
    if (savedLang && translations[savedLang]) {
      return savedLang;
    }

    // Try to get from browser language
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) {
      return browserLang;
    }

    return defaultLanguage;
  };

  const [language, setLanguageState] = useState(getInitialLanguage);
  const [visibleLanguageCodes, setVisibleLanguageCodesState] = useState(getInitialVisibleLanguages);

  // Get current translations object
  const t = translations[language] || translations[defaultLanguage];

  // Sync language from API on mount (only if logged in)
  useEffect(() => {
    const syncLanguageFromApi = async () => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const apiLang = await getLanguagePreference();
        const localLang = localStorage.getItem('language');

        if (apiLang && apiLang !== 'fr' && translations[apiLang]) {
          // DB has a language (not default 'fr') - load it to localStorage
          if (apiLang !== language) {
            console.log('Syncing language from API:', apiLang);
            setLanguageState(apiLang);
            localStorage.setItem('language', apiLang);
          }
        } else if (localLang && localLang !== 'fr') {
          // DB is empty/default but localStorage has a language - push to API
          console.log('Pushing language from localStorage to API:', localLang);
          saveLanguagePreference(localLang);
        }
      }
    };
    syncLanguageFromApi();
  }, []);

  // Change language and save to localStorage + API
  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem('language', lang);
      // Update html lang attribute
      document.documentElement.lang = lang;

      // Sync to API if logged in
      if (localStorage.getItem('userId')) {
        saveLanguagePreference(lang);
      }
    }
  }, []);

  // Translate function that supports nested keys like 'nav.discover'
  const translate = useCallback((key, params = {}) => {
    const value = getTranslation(t, key);

    if (typeof value === 'function') {
      return value(params);
    }

    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return Object.entries(params).reduce((str, [key, val]) => {
        return str.replace(new RegExp(`{${key}}`, 'g'), val);
      }, value);
    }

    return value;
  }, [t]);

  // Set html lang attribute on mount and language change
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Set visible languages
  const setVisibleLanguageCodes = useCallback((codes) => {
    // Ensure current language is always visible
    const finalCodes = codes.includes(language) ? codes : [...codes, language];
    setVisibleLanguageCodesState(finalCodes);
    localStorage.setItem('cabazaVisibleLanguages', JSON.stringify(finalCodes));
  }, [language]);

  // Get filtered languages for dropdown
  const visibleLanguages = languages.filter(l => visibleLanguageCodes.includes(l.code));

  const value = {
    language,
    setLanguage,
    t,
    translate,
    languages,
    visibleLanguages,
    visibleLanguageCodes,
    setVisibleLanguageCodes,
    translations
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Shorthand hook for just translations
export const useTranslation = () => {
  const { t, translate, language } = useLanguage();
  return { t, translate, language };
};

export default LanguageContext;
